import React, { useState, useEffect } from 'react';
import EnhancedFishCard from '../components/EnhancedFishCard.js';
import FishService from '../services/fishService.js'; // Import FishService
import { fishDataFromCsv as localFishData } from '../data/fishDataFromCsv.js';

const Shop = () => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [currentFishData, setCurrentFishData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFish = async () => {
      try {
        setLoading(true);
        const data = await FishService.getAllFish();
        setCurrentFishData(data);
        setLoading(false);
      } catch (err) {
        console.warn('Falling back to local fish data. API error:', err?.message || err);
        setCurrentFishData(localFishData);
        setError(null);
        setLoading(false);
      }
    };

    fetchFish();

    // Optionally, subscribe to real-time updates if needed
    // const unsubscribe = FishService.subscribeToFishUpdates((fish) => {
    //   setCurrentFishData(fish);
    // });
    // return () => unsubscribe();
  }, []);

  // Get unique categories for filter dropdown
  const categories = [...new Set(currentFishData.map(f => f.category).filter(Boolean))];

  if (loading) {
    return <div style={{ paddingTop: '80px', textAlign: 'center', color: 'var(--foreground)' }}>Loading fish...</div>;
  }

  if (error) {
    return <div style={{ paddingTop: '80px', textAlign: 'center', color: 'var(--danger)' }}>Error: {error}</div>;
  }

  const filteredFish = currentFishData.filter(fish => {
    // Filter by care level
    const careLevel = fish.care_level || fish.careLevel;
    if (filter !== 'all' && careLevel !== filter) return false;
    
    // Filter by search term
    if (searchTerm && 
        !fish.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !fish.scientific_name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !fish.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Filter by category
    if (categoryFilter !== 'all' && fish.category !== categoryFilter) return false;
    
    // Filter by availability
    if (availabilityFilter === 'available' && (!fish.is_available || fish.stock_quantity <= 0)) return false;
    if (availabilityFilter === 'unavailable' && (fish.is_available && fish.stock_quantity > 0)) return false;
    
    return true;
  });

  const sortedFish = [...filteredFish].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'care-level':
        const careLevels = { beginner: 1, intermediate: 2, advanced: 2.5, expert: 3 };
        const aLevel = a.care_level || a.careLevel;
        const bLevel = b.care_level || b.careLevel;
        return careLevels[aLevel] - careLevels[bLevel];
      default:
        return 0;
    }
  });

  return (
    <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container" style={{ paddingBottom: '80px' }}>
        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '50px',
            background: 'hsl(200, 85%, 25%, 0.1)',
            color: 'var(--primary)',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '1.5rem' }}>🐠</span>
            <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Premium Collection</span>
          </div>
          <h1 className="font-display" style={{
            fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            fontWeight: 'bold',
            color: 'var(--foreground)',
            marginBottom: '1rem'
          }}>
            Fish Collection
          </h1>
          <p style={{
            color: 'var(--muted-foreground)',
            maxWidth: '42rem',
            margin: '0 auto',
            fontSize: '1.125rem',
            lineHeight: '1.6'
          }}>
            Explore our curated selection of premium marine fish from the pristine waters of the Maldives.
          </p>
        </div>
        
        {/* Filters and Search */}
        <div className="reef-card" style={{ marginBottom: '3rem', padding: '2rem' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1.5rem', 
            alignItems: 'end' 
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: 'var(--foreground)',
                fontSize: '0.875rem'
              }}>
                🔍 Search Fish:
              </label>
              <input
                type="text"
                placeholder="Search fish, species, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '1rem',
                  background: 'var(--background)',
                  color: 'var(--foreground)',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: 'var(--foreground)',
                fontSize: '0.875rem'
              }}>
                🐠 Category:
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '1rem',
                  background: 'var(--background)',
                  color: 'var(--foreground)',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: 'var(--foreground)',
                fontSize: '0.875rem'
              }}>
                🏆 Care Level:
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '1rem',
                  background: 'var(--background)',
                  color: 'var(--foreground)',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: 'var(--foreground)',
                fontSize: '0.875rem'
              }}>
                📦 Availability:
              </label>
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '1rem',
                  background: 'var(--background)',
                  color: 'var(--foreground)',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Fish</option>
                <option value="available">In Stock</option>
                <option value="unavailable">Out of Stock</option>
              </select>
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600',
                color: 'var(--foreground)',
                fontSize: '0.875rem'
              }}>
                📊 Sort By:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '1rem',
                  background: 'var(--background)',
                  color: 'var(--foreground)',
                  cursor: 'pointer'
                }}
              >
                <option value="name">Name (A-Z)</option>
                <option value="price-low">Price (Low to High)</option>
                <option value="price-high">Price (High to Low)</option>
                <option value="care-level">Care Level</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div style={{ 
          marginBottom: '2rem', 
          textAlign: 'center',
          padding: '1rem',
          background: 'var(--muted)',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--border)'
        }}>
          <p style={{ 
            color: 'var(--foreground)', 
            fontSize: '1.125rem', 
            fontWeight: '600',
            margin: 0
          }}>
            🐠 Showing <span style={{ color: 'var(--primary)' }}>{sortedFish.length}</span> of <span style={{ color: 'var(--primary)' }}>{currentFishData.length}</span> fish
          </p>
        </div>

        {/* Fish Grid */}
        <div 
          className="fish-grid"
          style={{ 
            marginBottom: '3rem'
          }}
        >
          {sortedFish.map((fish, index) => (
            <div 
              key={fish.id}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <EnhancedFishCard fish={fish} />
            </div>
          ))}
        </div>

        {/* No Results */}
        {sortedFish.length === 0 && (
          <div className="reef-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🐠</div>
            <h3 className="font-display" style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: 'var(--foreground)', 
              marginBottom: '0.5rem' 
            }}>
              No fish found
            </h3>
            <p style={{ color: 'var(--muted-foreground)', fontSize: '1rem', marginBottom: '1.5rem' }}>
              Try adjusting your search or filter criteria to find your perfect marine companion.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('all');
                setFilter('all');
                setAvailabilityFilter('all');
                setSortBy('name');
              }}
              className="btn-ghost"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem'
              }}
            >
              <span>🔄</span>
              Clear All Filters
            </button>
          </div>
        )}

        {/* Care Level Guide */}
        <div className="reef-card" style={{ marginTop: '4rem', padding: '2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '50px',
              background: 'hsl(12, 85%, 60%, 0.1)',
              color: 'var(--coral)',
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>🏆</span>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Care Level Guide</span>
            </div>
            <h3 className="font-display" style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: 'var(--foreground)',
              marginBottom: '0.5rem'
            }}>
              Find Your Perfect Match
            </h3>
            <p style={{ color: 'var(--muted-foreground)' }}>
              Choose fish that match your aquarium experience level
            </p>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {[
              {
                level: 'BEGINNER',
                icon: '🌱',
                color: 'hsl(142, 76%, 36%)',
                bg: 'hsl(142, 76%, 36%, 0.1)',
                description: 'Perfect for new aquarists. Hardy and forgiving fish that adapt well to various conditions.'
              },
              {
                level: 'INTERMEDIATE',
                icon: '⭐',
                color: 'hsl(45, 93%, 47%)',
                bg: 'hsl(45, 93%, 47%, 0.1)',
                description: 'Requires some experience. Moderate care requirements and stable water conditions.'
              },
              {
                level: 'ADVANCED',
                icon: '🔥',
                color: 'hsl(24, 74%, 58%)',
                bg: 'hsl(24, 74%, 58%, 0.1)',
                description: 'For experienced aquarists. Specific dietary and environmental needs.'
              },
              {
                level: 'EXPERT',
                icon: '👑',
                color: 'hsl(348, 83%, 47%)',
                bg: 'hsl(348, 83%, 47%, 0.1)',
                description: 'Challenging species requiring expert knowledge and pristine conditions.'
              }
            ].map((care, index) => (
              <div key={care.level} style={{ 
                textAlign: 'center',
                padding: '1.5rem',
                background: care.bg,
                borderRadius: 'var(--radius)',
                border: `1px solid ${care.color}40`
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{care.icon}</div>
                <span style={{ 
                  display: 'inline-block', 
                  marginBottom: '1rem',
                  padding: '0.5rem 1rem',
                  background: care.color,
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.5px'
                }}>
                  {care.level}
                </span>
                <p style={{ 
                  color: 'var(--foreground)', 
                  fontSize: '0.875rem', 
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  {care.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
